/**
 * SpreadDiagram — SVG layout of a spread's card positions.
 *
 * Without cards: renders numbered placeholder slots at each position's layoutHint.
 * With cards: renders actual card images at each slot (reversed = rotated 180°).
 *
 * The SVG uses a fixed 400×400 viewBox with padding so edge positions
 * (e.g. year-ahead at y=0.02) are never clipped. Scales to fill width via CSS.
 */

const VIEWBOX = 400;
const CARD_W = 44;
const CARD_H = Math.round(CARD_W / 0.6); // ~73px — standard card ratio
const PAD = Math.ceil(CARD_H / 2) + 6;   // enough room at every edge
const DRAW_W = VIEWBOX - 2 * PAD;
const DRAW_H = VIEWBOX - 2 * PAD;

interface SpreadPosition {
  index: number;
  label: string;
  meaning: string;
  layoutHint?: { x: number; y: number; rotation?: number };
}

interface Spread {
  id: string;
  positions: SpreadPosition[];
}

export interface DrawnSlot {
  positionIndex: number;
  imageUrl: string;
  reversed: boolean;
}

interface SpreadDiagramProps {
  spread: Spread;
  cards?: DrawnSlot[];
  width?: number;
}

export function SpreadDiagram({ spread, cards, width }: SpreadDiagramProps) {
  return (
    <svg
      viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
      className="tarot-spread-diagram"
      style={width != null ? { width, height: width } : undefined}
      aria-label={`${spread.id} spread layout`}
    >
      {spread.positions.map((pos) => {
        const hint = pos.layoutHint ?? { x: 0.5, y: 0.5 };
        const cx = Math.round(PAD + hint.x * DRAW_W);
        const cy = Math.round(PAD + hint.y * DRAW_H);
        const slotRotation = hint.rotation ?? 0;
        const x = cx - CARD_W / 2;
        const y = cy - CARD_H / 2;
        const drawn = cards?.find((c) => c.positionIndex === pos.index);

        return (
          <g
            key={pos.index}
            transform={slotRotation ? `rotate(${slotRotation} ${cx} ${cy})` : undefined}
          >
            {drawn ? (
              <g transform={drawn.reversed ? `rotate(180 ${cx} ${cy})` : undefined}>
                <image
                  href={drawn.imageUrl}
                  x={x}
                  y={y}
                  width={CARD_W}
                  height={CARD_H}
                  preserveAspectRatio="xMidYMid slice"
                  style={{ borderRadius: '4px' }}
                />
              </g>
            ) : (
              <>
                <rect
                  x={x}
                  y={y}
                  width={CARD_W}
                  height={CARD_H}
                  rx={4}
                  fill="rgba(239,241,243,0.08)"
                  stroke="rgba(239,241,243,0.25)"
                  strokeWidth={1.5}
                />
                <text
                  x={cx}
                  y={cy + 1}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={16}
                  fontWeight="600"
                  fontFamily="Outfit, system-ui, sans-serif"
                  fill="rgba(239,241,243,0.65)"
                >
                  {pos.index + 1}
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}
